import type { Grant, RecursoDoPlano, SessionUser, TenantSummary } from '@levitas/contracts';

/**
 * A última sessão conhecida, para o Levitas abrir sem rede (seção 9.5).
 *
 * Sem isto, abrir o aplicativo no estacionamento da igreja — onde não há sinal
 * — jogaria a pessoa na tela de login, porque a renovação do token não teria
 * como chegar ao servidor. Com isto, ela vê a própria escala, guardada no
 * aparelho, e responde quando a rede voltar.
 *
 * **Nenhum token é guardado aqui.** O que fica é quem a pessoa é, em qual
 * igreja e o que ela pode ver — o suficiente para desenhar a tela, e nada que
 * autorize uma chamada à API. Toda escrita continua exigindo o servidor.
 *
 * E só vale sem rede: assim que há conexão, quem manda é o servidor. Se ele
 * recusar a sessão, isto é apagado junto.
 */

const CHAVE = 'levitas:sessao';

export interface SessaoLembrada {
  usuario: SessionUser;
  igreja: TenantSummary | null;
  igrejas: TenantSummary[];
  permissoes: Grant[];
  /**
   * O que o plano abre. Guardado junto para a primeira tela depois de um F5
   * já sair com o menu certo, sem piscar o que a igreja não tem.
   */
  recursos?: Partial<Record<RecursoDoPlano, boolean>>;
}

export function lembrarSessao(sessao: SessaoLembrada): void {
  try {
    localStorage.setItem(CHAVE, JSON.stringify(sessao));
  } catch {
    // Armazenamento bloqueado: o produto funciona igual, só não abre sem rede.
  }
}

export function sessaoLembrada(): SessaoLembrada | null {
  try {
    const cru = localStorage.getItem(CHAVE);
    if (!cru) return null;

    const dados = JSON.parse(cru) as SessaoLembrada;
    return dados.usuario ? dados : null;
  } catch {
    return null;
  }
}

/** Ao sair — ou quando o servidor recusa a sessão — nada disto sobrevive. */
export function esquecerSessao(): void {
  try {
    localStorage.removeItem(CHAVE);
  } catch {
    // Ver acima: não há o que fazer, e não há o que quebrar.
  }
}
